<template>
  <b-modal
    v-model="visible"
    :title="$t('whatsapp.template.title')"
    size="lg"
    centered
    :busy="store.templateSending"
    @shown="onShown"
    @hidden="reset"
  >
    <b-alert
      :show="!!store.templateSendError"
      variant="danger"
      class="py-1 px-2 small"
      dismissible
      @dismissed="store.setTemplateSendError(null)"
    >
      {{ store.templateSendError }}
    </b-alert>
    <b-alert
      :show="store.templatesStale"
      variant="warning"
      class="py-1 px-2 small"
    >
      {{ $t('whatsapp.template.stale-status') }}
    </b-alert>

    <!-- ------------------------------------------------ 1. the contact -->
    <section v-if="!fixedContact" class="mb-3">
      <div class="small text-uppercase text-muted mb-1">
        {{ $t('whatsapp.template.contact') }}
      </div>
      <!-- Candidate mode (GID-2711): the caller already knows the contacts
           (a partner's), so there is nothing to search — we only show which of
           them opted in. Free search stays the default everywhere else. -->
      <b-form
        v-if="!contactCandidates"
        class="d-flex mb-2"
        @submit.prevent="search"
      >
        <b-form-input
          v-model="query"
          size="sm"
          class="mr-1"
          :placeholder="$t('whatsapp.template.search-placeholder')"
        />
        <b-button
          size="sm"
          type="submit"
          variant="outline-primary"
          :disabled="query.trim().length < 2 || store.contactSearchLoading"
        >
          {{ $t('whatsapp.template.search') }}
        </b-button>
      </b-form>

      <div
        v-for="candidate in store.contactResults"
        :key="candidate.contactId"
        class="border rounded p-1 mb-1 d-flex align-items-center"
        :class="{
          'border-primary':
            selectedContact &&
            selectedContact.contactId === candidate.contactId,
        }"
      >
        <div class="flex-grow-1 mr-1 small">
          <div>{{ candidate.name || candidate.contactId }}</div>
          <div class="text-muted">
            {{ candidate.waPhone || $t('whatsapp.template.no-number') }}
            <span v-if="candidate.email">· {{ candidate.email }}</span>
          </div>
        </div>
        <b-button
          size="sm"
          variant="outline-primary"
          :disabled="!candidate.waPhone"
          @click="selectedContact = candidate"
        >
          {{ $t('whatsapp.template.choose') }}
        </b-button>
      </div>
      <b-spinner v-if="store.contactSearchLoading" small />
      <div
        v-else-if="
          !store.contactResults.length && (searched || contactCandidates)
        "
        class="small text-muted font-italic"
      >
        <!-- In candidate mode an empty list has one meaning, and it is not
             "not found": none of this partner's contacts consented. -->
        {{
          contactCandidates
            ? $t('whatsapp.template.no-consent')
            : $t('whatsapp.template.no-contacts')
        }}
      </div>
    </section>

    <!-- ---------------------------------------------- 1b. the linked jobs -->
    <section v-if="jobLinks.length" class="mb-3">
      <div class="small text-uppercase text-muted mb-1">
        {{ $t('whatsapp.template.linked-jobs') }}
      </div>
      <div class="small">
        <b-badge
          v-for="job in jobLinks"
          :key="job.apiId"
          variant="light"
          class="border mr-1 mb-1"
        >
          {{ job.name || job.apiId }}
        </b-badge>
      </div>
      <div class="small text-muted mt-1">
        {{ $t('whatsapp.template.linked-jobs-hint') }}
      </div>
      <b-alert
        :show="tooManyJobs"
        variant="warning"
        class="py-1 px-2 small mt-1 mb-0"
      >
        {{ $t('whatsapp.template.single-job-required') }}
      </b-alert>
    </section>

    <!-- ----------------------------------------------- 2. the template -->
    <section v-if="targetContact" class="mb-3">
      <div class="small text-uppercase text-muted mb-1">
        {{ $t('whatsapp.template.template') }}
        <span v-if="!fixedContact">— {{ targetContactLabel }}</span>
      </div>

      <div
        v-if="!approvedTemplates.length"
        class="small text-muted font-italic"
      >
        {{ $t('whatsapp.template.none-approved') }}
      </div>
      <template v-else>
        <b-form-select
          v-model="templatePath"
          size="sm"
          class="mb-2"
          :options="templateOptions"
        />
        <b-form-select
          v-if="localeOptions.length > 1"
          v-model="locale"
          size="sm"
          class="mb-2"
          :options="localeOptions"
        />

        <template v-if="selectedLang">
          <b-form-group
            v-for="index in selectedLang.parameterCount"
            :key="index"
            :label="parameterLabel(index)"
            label-size="sm"
            class="mb-2"
          >
            <!-- A variable with a fixed value set is picked, not typed: the
                 wording is part of the sentence and has to stay grammatical. -->
            <b-form-select
              v-if="parameterOptions(index)"
              v-model="parameters[index - 1]"
              size="sm"
              :options="parameterOptions(index)"
            >
              <template #first>
                <b-form-select-option value="" disabled>
                  {{ $t('whatsapp.template.choose-option') }}
                </b-form-select-option>
              </template>
            </b-form-select>
            <b-form-input v-else v-model="parameters[index - 1]" size="sm" />
          </b-form-group>

          <!-- Only when the caller did not already hand us the jobs. -->
          <b-form-group
            v-if="!jobLinks.length"
            :label="$t('whatsapp.template.job')"
            label-size="sm"
            class="mb-2"
          >
            <b-form-input
              v-model="jobName"
              size="sm"
              :placeholder="$t('whatsapp.template.job-placeholder')"
            />
          </b-form-group>

          <div class="small text-uppercase text-muted mb-1">
            {{ $t('whatsapp.template.preview') }}
          </div>
          <!-- Interpolated, never v-html — same rule as message bodies. -->
          <div class="border rounded p-2 small preview">{{ preview }}</div>
        </template>
      </template>
    </section>

    <template #modal-footer>
      <b-button variant="outline-secondary" @click="visible = false">
        {{ $t('whatsapp.template.cancel') }}
      </b-button>
      <b-button
        variant="primary"
        :disabled="!canSend || store.templateSending"
        @click="send"
      >
        {{ $t('whatsapp.template.send') }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { getModule } from 'vuex-module-decorators';
import { WhatsappModule } from '@/store/whatsapp.module';

/**
 * Variable sources that describe exactly ONE job. Comma-joining several jobs
 * into one message works for order numbers and nothing else — a price or a
 * site address would attribute every linked job to whichever came first.
 */
const SINGLE_JOB_SOURCES = ['customer.', 'job.price', 'job.address'];

/** Body parameters carry no currency symbol — the template text says "Euro". */
function formatEuroAmount(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return null;
  return amount.toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Business-initiated template message (GID-2697, M6).
 *
 * Templates are the only WhatsApp message deliverable without an open 24h
 * window, so this is the "start a conversation" path. Two blocks are enforced
 * server-side and merely surfaced here: the contact search returns ONLY
 * contacts who opted in, and the send refuses non-consented contacts and
 * template variants Meta has not approved.
 */
export default {
  name: 'TemplateSendModal',
  props: {
    /** Preselected contact ({contactId, name}); skips the search step. */
    fixedContact: { type: Object, default: null },
    /**
     * Offer exactly these contacts instead of a free search (GID-2711) — the
     * admin partner view passes the partner's contacts. Consent still decides:
     * the server returns only the consented ones, and only those are listed.
     */
    contactCandidates: { type: Array, default: null },
    /**
     * Jobs to link the message to (GID-2711). Accepts the partner-view job rows
     * (`api_id` / `job_name`) as they come. Linking is what makes the message
     * show up in each job's activity feed.
     */
    jobs: { type: Array, default: () => [] },
  },
  data() {
    return {
      visible: false,
      query: '',
      searched: false,
      selectedContact: null,
      templatePath: null,
      locale: null,
      parameters: [],
      jobName: '',
    };
  },
  computed: {
    store() {
      return getModule(WhatsappModule, this.$store);
    },
    targetContact() {
      return this.fixedContact || this.selectedContact;
    },
    /**
     * The caller's job rows, normalised — partner-view shape or {apiId,name}.
     * Customer name and city ride along for the appointment templates: the
     * partner has to phone that customer, so the message names them. The
     * purchasing price and the site's postcode ride along for the offer
     * template, which has to state what the job pays and where it is.
     */
    jobLinks() {
      return (this.jobs || [])
        .map((job) => ({
          apiId: job.apiId ?? job.api_id,
          name: job.name ?? job.job_name ?? null,
          customerLastName: job.customerLastName ?? job.cust_lastname ?? null,
          customerCity: job.customerCity ?? job.cust_shipping_city ?? null,
          customerPostalCode:
            job.customerPostalCode ?? job.cust_shipping_postal_code ?? null,
          pricePurchasing:
            job.pricePurchasing ?? job.price_purchasing_sum ?? null,
        }))
        .filter((job) => job.apiId);
    },
    /**
     * Templates naming the customer, the pay or the site describe exactly one
     * job — comma-joining several order numbers into "…beim Kunden Müller in
     * Köln" would attribute every one of them to whichever job came first.
     */
    requiresSingleJob() {
      return (this.selectedTemplate?.variables || []).some((variable) =>
        SINGLE_JOB_SOURCES.some((prefix) =>
          String(variable?.source || '').startsWith(prefix),
        ),
      );
    },
    tooManyJobs() {
      return this.requiresSingleJob && this.jobLinks.length > 1;
    },
    targetContactLabel() {
      const contact = this.targetContact;
      return contact ? contact.name || contact.contactId : '';
    },
    /** Only templates with at least one Meta-APPROVED language variant. */
    approvedTemplates() {
      return this.store.templates
        .map((template) => ({
          ...template,
          langs: template.langs.filter(
            (lang) => lang.metaStatus === 'APPROVED',
          ),
        }))
        .filter((template) => template.langs.length);
    },
    templateOptions() {
      return this.approvedTemplates.map((template) => ({
        value: template.path,
        text: template.name,
      }));
    },
    selectedTemplate() {
      return (
        this.approvedTemplates.find(
          (template) => template.path === this.templatePath,
        ) || null
      );
    },
    localeOptions() {
      return (this.selectedTemplate?.langs || []).map((lang) => ({
        value: lang.locale,
        text: lang.locale,
      }));
    },
    selectedLang() {
      return (
        this.selectedTemplate?.langs.find(
          (lang) => lang.locale === this.locale,
        ) || null
      );
    },
    preview() {
      if (!this.selectedLang) return '';
      return this.selectedLang.content.replace(
        /\{\{(\d+)\}\}/g,
        (match, index) => this.parameters[Number(index) - 1] || match,
      );
    },
    canSend() {
      return (
        this.targetContact &&
        this.selectedLang &&
        !this.tooManyJobs &&
        this.parameters.length === this.selectedLang.parameterCount &&
        this.parameters.every((value) => value && value.trim())
      );
    },
  },
  watch: {
    templatePath() {
      this.locale = this.localeOptions[0]?.value ?? null;
    },
    selectedLang(lang) {
      this.parameters = lang ? new Array(lang.parameterCount).fill('') : [];
      this.prefillParameters();
    },
  },
  methods: {
    open() {
      this.visible = true;
    },
    onShown() {
      this.store.fetchTemplates();
      if (this.contactCandidates) {
        this.store.lookupContacts(
          this.contactCandidates.map((contact) => contact.id).filter(Boolean),
        );
      }
    },
    reset() {
      this.query = '';
      this.searched = false;
      this.selectedContact = null;
      this.templatePath = null;
      this.locale = null;
      this.parameters = [];
      this.jobName = '';
      this.store.setTemplateSendError(null);
      this.store.setContactResults([]);
    },
    async search() {
      await this.store.searchContacts(this.query);
      this.searched = true;
    },
    /**
     * Best-effort prefill from `template.context.variables`
     * (e.g. `[{index: 1, source: 'contact.name'}]`) — everything stays
     * editable; the admin sees exactly what will be sent.
     */
    prefillParameters() {
      const variables = this.selectedTemplate?.variables;
      const contact = this.targetContact;
      if (!Array.isArray(variables) || !contact) return;
      // Meta rejects newlines and tabs in a body parameter, so several order
      // numbers go in as one comma-separated value.
      const jobNumbers = this.jobLinks
        .map((job) => job.name)
        .filter(Boolean)
        .join(', ');
      // Customer fields only make sense for a single job — `tooManyJobs`
      // blocks the send in that case rather than prefilling a wrong customer.
      const soleJob = this.jobLinks.length === 1 ? this.jobLinks[0] : null;
      for (const variable of variables) {
        const index = Number(variable?.index) - 1;
        if (!(index >= 0 && index < this.parameters.length)) continue;
        if (variable.source === 'contact.name' && contact.name) {
          this.$set(this.parameters, index, contact.name);
        }
        if (variable.source === 'job.name' && jobNumbers) {
          this.$set(this.parameters, index, jobNumbers);
        }
        if (
          variable.source === 'customer.lastName' &&
          soleJob?.customerLastName
        ) {
          this.$set(this.parameters, index, soleJob.customerLastName);
        }
        if (variable.source === 'customer.city' && soleJob?.customerCity) {
          this.$set(this.parameters, index, soleJob.customerCity);
        }
        if (variable.source === 'job.price') {
          const amount = formatEuroAmount(soleJob?.pricePurchasing);
          if (amount) this.$set(this.parameters, index, amount);
        }
        if (variable.source === 'job.address') {
          // The site, not the billing address, and never the street: the
          // partner has not accepted the job yet.
          const address = [soleJob?.customerPostalCode, soleJob?.customerCity]
            .filter(Boolean)
            .join(' ');
          if (address) this.$set(this.parameters, index, address);
        }
      }
    },
    /**
     * The fixed value set for a variable, when `context.variables` defines one
     * (`options: [{value, text}]`) — otherwise the parameter is free text.
     */
    parameterOptions(index) {
      const variables = this.selectedTemplate?.variables;
      const options = Array.isArray(variables)
        ? variables.find((variable) => Number(variable?.index) === index)
            ?.options
        : null;
      return Array.isArray(options) && options.length ? options : null;
    },
    parameterLabel(index) {
      const variables = this.selectedTemplate?.variables;
      const label = Array.isArray(variables)
        ? variables.find((variable) => Number(variable?.index) === index)?.label
        : null;
      return label || `{{${index}}}`;
    },
    async send() {
      const conversationId = await this.store.sendTemplateMessage({
        contactId: this.targetContact.contactId,
        templatePath: this.templatePath,
        locale: this.locale,
        parameters: this.parameters.map((value) => value.trim()),
        ...(this.jobName.trim() ? { jobName: this.jobName.trim() } : {}),
        ...(this.jobLinks.length
          ? { jobApiIds: this.jobLinks.map((job) => job.apiId) }
          : {}),
      });
      if (conversationId) {
        this.visible = false;
        this.$emit('sent', conversationId);
      }
    },
  },
};
</script>

<style scoped>
.preview {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  background-color: #f8f9fa;
}
</style>
